/**
 * Created by y.belinsky on 12/16/16.
 */

import { Injectable, Injector } from '@angular/core';

import { Invoice } from '@shared/classes/invoice.class';
import { InvoicePayment } from '@shared/classes/invoice-payment.class';
import { InvoiceEntry, SpecialTaxing } from '@shared/classes/invoice-entry.class';

import { UPDATES_TYPES } from '@shared/constants/updates-types.const';
import { SYNC_ITEM_STATES } from '@shared/constants/sync-statuses.const';

import { UuidService } from '../utils/uuid.utils';
import { SyncService } from './sync.service';
import { DbDaoService } from '../db/db-dao.service';
import moment from 'moment';
import { SecurityService } from './security.service';
import { PaymentMethod } from '@shared/classes/payment-method.class';
import { PaymentService } from './payment.service';
import { PRODUCT_TYPES } from '@shared/constants/product-types';
import { CustomerService } from './customers.service';
import { LoadingService } from './loading.service';
import { Product } from '@shared/classes/product.class';
import { ProductVariant } from '@shared/classes/product-variant.class';
import { PaymentResult } from '@shared/classes/payment-result.class';
import { PAYMENT_PROVIDERS, PAYMENT_TRANSACTION } from '@shared/constants/payment-providers.const';
import { SyncListItem } from '@shared/classes/sync-list-item.class';
import { LogService } from './logger/log.service';
import { PosSettingsService } from './pos-settings.service';
import { ReferenceEntity } from '@shared/classes/reference-entity';
import { ICancellationInvoiceParams } from '@shared/interfaces/cancellation-invoice-params.interface';
import { InvoicesApiService } from '../api/invoices-api.service';
import { InvoicePartiallyCancel } from '@shared/classes/invoice/invoice-partially-cancel.class';
import { PaymentError } from '@shared/constants/payment-error.enum';
import { DefaultPaymentMethods } from '@shared/constants/default-payment-methods.enum';
import { GiftCard } from '@features/gift-card/gift-card';
import { CancellationTypes } from '@shared/constants/cancellation-types.enum';
import { IMergeUpdateAndActiveInvoiceEntries } from '@shared/interfaces/merge-invoice-entries.interface';
import { InvoiceDetailsTaxItem } from '@features/invoice-details/interfaces/invoice-details-tax-item';
import { MathUtils } from '../utils/math.utils';
import { LOCALE } from '@shared/constants/locale.const';
import { ISplitInvoices } from '@shared/interfaces/split-invoices.interface';
import { MultipleGuestsService } from '@core/services/system/multiple-guests/multiple-guests.service';
import { INVOICE_TYPES } from '@shared/constants/invoice-types';
import {
  DISCOUNT_CODE_TYPES,
  REGEXPS,
  SplitInvoiceTypes,
  StorageKeys,
} from '@shared/constants';
import {
  arrayIsNotEmpty,
  greaterThan,
  group,
  isNull,
  lessOrEqualsThan,
  oneOf,
  or,
  query,
} from '@paymash/capacitor-database-plugin';
import { ignoreImageProps } from '@core/services/system/logger';
import { LocalStorage } from '../utils/localstorage.utils';
import { IActiveTerminal } from '@shared/interfaces';
import { DbResponse } from '@core/services/db/db-dao.utils';
import { EmployeesFacadeStore } from '@state/employees';
import { Employee } from '@shared/classes/employee.class';
import { AdyenTippingService } from '@core/services/system/adyen/services/adyen-tipping.service';
import { Company } from '@shared/classes/company.class';
import { trustValue } from '@core/services';
import { InvoiceForLogsClass } from '@shared/classes/invoice/invoice-for-logs.class';
import { ShiftsService } from '@core/services/system/shifts.service';
import { InvoicesFacadeStore } from '@state/invoices';
import { AlertService } from '@core/services/system/alert.service';
import { TranslateService } from '@ngx-translate/core';
import { ROUTE_URLS } from '@routing/route-urls.const';
import { NavController } from '@ionic/angular';
import { CartService } from '@core/services/system/cart/cart.service';
import { ICustomerDisplayService } from '@core/services/system/customer-display';

/**
 * Delay between the customer-display reset (processInvoiceChange(undefined)) and pushing the copied
 * draft when copying a paid invoice. Aligned with the display's cart auditTime so the reset renders
 * before the copy is shown.
 */
const CUSTOMER_DISPLAY_COPY_RESET_DELAY_MS = 150;

@Injectable()
export class InvoicesService {
  private logger = this.logService.createLogger('InvoicesService');
  private oldInvoicesRemovalInProgress = false;

  constructor(
    public UuidService: UuidService,
    public SyncService: SyncService,
    public DbDaoService: DbDaoService,
    public SecurityService: SecurityService,
    public PaymentService: PaymentService,
    public LoadingService: LoadingService,
    public CustomerService: CustomerService,
    private posSettingsService: PosSettingsService,
    public invoicesApiService: InvoicesApiService,
    private multipleGuestsService: MultipleGuestsService,
    private localStorage: LocalStorage,
    private logService: LogService,
    private facadeEmployeeState: EmployeesFacadeStore,
    private adyenTippingService: AdyenTippingService,
    private shiftsService: ShiftsService,
    private facadeInvoiceService: InvoicesFacadeStore,
    private alertService: AlertService,
    private translateService: TranslateService,
    private navController: NavController,
    private inject: Injector,
  ) {}

  public createInvoice(): Invoice {
    const invoiceUUID = this.UuidService.generate();
    const newInvoiceData: Invoice = new Invoice({
      uuid: invoiceUUID,
      publicUuid: invoiceUUID.replace(/-/g, ''),
      // TODO ADJUST INVOICE PREPARATION
      invoiceId: null,
      isDraft: true,
    });

    const activeEmployee: Employee = this.facadeEmployeeState.activeEmployeeSnapshot;
    if (activeEmployee) {
      newInvoiceData.employee = new ReferenceEntity(activeEmployee?.uuid);
    }
    const activeStore = this.SecurityService.getActiveStore();
    if (activeStore) {
      const store = new ReferenceEntity(activeStore?.uuid);
      newInvoiceData.store = store;
      newInvoiceData.inventoryStore = store;
    }
    const company = this.SecurityService.getLoggedCompanyData();
    if (company) {
      newInvoiceData.isNet = company.isInvoiceNet;
    }

    newInvoiceData.setLocalCreationDate();

    this.logger.info('Invoice has been created', { invoiceUuid: newInvoiceData?.uuid });
    return newInvoiceData;
  }

  async addPaymentToInvoice(
    invoice: Invoice,
    method: string,
    amount: any,
    amountGiven: any,
    transactionType: string,
    giftCard?: GiftCard,
    result?: PaymentResult
  ): Promise<Invoice> {
    this.logger.debug('Add payment to invoice', { invoiceUuid: invoice?.uuid });
    const newInvoicePayment: InvoicePayment = new InvoicePayment({
      uuid: this.UuidService.generate(),
      method,
      amount: parseFloat(amountGiven),
      amountGiven: 0,
      merchantReceipt: null,
      cardholderReceipt: null,
      giftCard: method === DefaultPaymentMethods.GIFTCARD ? giftCard : null,
    });

    newInvoicePayment.setLocalCreationDate();
    newInvoicePayment.increaseLocalModificationDate();
    const employeeUuid: string = this.facadeEmployeeState.activeEmployeeSnapshot?.uuid;
    invoice.employee = new ReferenceEntity(employeeUuid);

    if (method === DefaultPaymentMethods.CASH && !result) {
      const isCashPayAllowed: boolean = this.checkCashPayAllowed();

      if (!isCashPayAllowed && !invoice?.isWebshop) {
        this.shiftsService.showMessageRequredOpenShift().then();
        // Benign "no open shift" signal (user already saw the message); Sentry drops it via `ignoreErrors`.
        // No info log here on purpose — the payment-flow catches already log the throw downstream.
        throw Error(PaymentError.SHIFT_MUST_BE_OPEN);
      }

      newInvoicePayment.amount = amount > amountGiven ? parseFloat(amountGiven) : parseFloat(amount);
      newInvoicePayment.amountGiven = parseFloat(amountGiven);
    } else if (this.isTerminalMethod(method)) {
      const data = await this.DbDaoService.getAllData(UPDATES_TYPES.PaymentMethod.type, { method });
      const paymentMethod = new PaymentMethod(data.data[0]);
      const paymentProvider = this.getProviderByMethodWithSupportedTwint(paymentMethod?.name);
      if (!!data?.data?.length && paymentMethod?.name && paymentProvider) {
        const amountToPay = amount > amountGiven ? parseFloat(amountGiven) : parseFloat(amount);
        const currency = this.SecurityService.getLoggedCompanyData()?.locale?.currency || '';
        const paymentResult: PaymentResult = !!result ? result : await this.PaymentService.makeTransaction({
          paymentProvider,
          amount: amountToPay,
          currency,
          transactionType,
          invoiceUuid: invoice?.uuid,
          paymentUuid: newInvoicePayment?.uuid,
          // TODO: PMH-3624
          // paymentMethod: method,
          paymentMethod: paymentMethod?.name,
        });
        const methodName: string = await this.getPaymentMethodByPaymentType(paymentResult.cardName, method);
        const isLegacyPosTipping: boolean = this.posSettingsService.getTippingStatus();
        newInvoicePayment.method = methodName;
        newInvoicePayment.setPaymentResultData(paymentResult, isLegacyPosTipping);
        this.logger.info('Payment method has been added to invoice from terminal', {
          methodName,
          paymentProvider: newInvoicePayment.paymentProvider,
          invoiceUuid: invoice?.uuid,
          amount,
          amountGiven,
        });

        if (this.adyenTippingService.isNeedAdyenNativeTippingAddFlow(paymentProvider, paymentResult)) {
          const { guestNumberForPay } = this.multipleGuestsService;
          invoice = this.adyenTippingService.setLegacyTippingToInvoice(
            invoice,
            paymentResult?.terminalTransaction?.nativeTippingAmount,
            guestNumberForPay
          );
        }
      }
    }

    return this.addPaymentAndCalculate(invoice, newInvoicePayment);
  }

  private isTerminalMethod = (method: string) =>
    method.includes('CUSTOM') ||
    method === DefaultPaymentMethods.SUMUP ||
    method === DefaultPaymentMethods.TWINT ||
    method === DefaultPaymentMethods.MYPOS;

  private getPaymentMethodByPaymentType(cardType: string, fallback: string): Promise<string> {
    const cartTypeValue = cardType?.toLowerCase().replace(REGEXPS.ANY_SPACES, '') ?? null;
    if (!cartTypeValue) {
      return Promise.resolve(fallback);
    }
    return this.DbDaoService.getAllData(UPDATES_TYPES.PaymentMethod.type, { deleted: false })
      .then((data) => {
        const currentPaymentMethod = data.data.find((paymentMethod) => {
          const method = paymentMethod?.method?.toLowerCase().replace(REGEXPS.ANY_SPACES, '');
          const name = paymentMethod?.name?.toLowerCase().replace(REGEXPS.ANY_SPACES, '');
          return (
            method?.indexOf(cartTypeValue) > -1 ||
            name?.indexOf(cartTypeValue) > -1 ||
            cartTypeValue?.indexOf(method) > -1 ||
            cartTypeValue?.indexOf(name) > -1
          );
        });
        // this.logger.debug('getPaymentMethodByPaymentType:cardType', { cardType });
        return currentPaymentMethod?.method ?? fallback;
      })
      .catch((err) => {
        this.logger.error(err, 'getPaymentMethodByPaymentType:DbDaoService:getAllDataFromCollection');
        return fallback;
      });
  }

  private checkCashPayAllowed(): boolean {
    return this.shiftsService.isCashPaymentAllowed;
  }

  private addPaymentAndCalculate(invoice: Invoice, payment: InvoicePayment, onlyCalculation?: boolean): Invoice {
    if (!onlyCalculation) {
      invoice.invoicePayments.push(payment);
    }
    const invoicePaymentsTotal = invoice.getPaymentsTotal();
    if (invoicePaymentsTotal >= invoice.amount) {
      invoice.setInvoiceAsPaid();
      if (invoice.customer && invoice.date) {
        this.CustomerService.updateLastVisitDate(invoice.customer, invoice.date);
      }
    }
    this.logger.info('The payment was added to the invoice', { payment, invoiceUuid: invoice?.uuid });
    return invoice;
  }

  getProviderByMethod(methodName: string): PAYMENT_PROVIDERS {
    if (!methodName) {
      return null;
    }

    const currentName = methodName.toLowerCase().replace(REGEXPS.ANY_SPACES, '');
    if (this.isPaymentMethod(currentName, 'sumup')) return PAYMENT_PROVIDERS.SUMUP;
    if (this.isPaymentMethod(currentName, 'ccvfly')) return PAYMENT_PROVIDERS.CCV;
    if (this.isPaymentMethod(currentName, 'terminalsix')) return PAYMENT_PROVIDERS.SIX;
    if (this.isPaymentMethod(currentName, 'terminalopi')) return PAYMENT_PROVIDERS.OPI;
    if (this.isPaymentMethod(currentName, 'myposglass')) return PAYMENT_PROVIDERS.MYPOSGLASS;
    if (this.isPaymentMethod(currentName, 'mypos')) return PAYMENT_PROVIDERS.MYPOS;
    if (this.isPaymentMethod(currentName, 'paymashpay')) return PAYMENT_PROVIDERS.PAYMASH_PAY;
    if (this.isPaymentMethod(currentName, 'softpos')) return PAYMENT_PROVIDERS.SOFTPOS;
    return null;
  }

  private getProviderByMethodWithSupportedTwint(methodName: string): PAYMENT_PROVIDERS {
    methodName = !!methodName ? methodName.toUpperCase() : methodName;
    if (methodName === DefaultPaymentMethods.TWINT) {
      const currency = this.SecurityService.getLoggedCompanyData()?.locale?.currency || '';
      if (currency === LOCALE.Currency.CHF) {
        const paymentProvider = this.getProviderByActiveTerminal();
        if (paymentProvider) {
          return paymentProvider;
        }
      }
    }
    return this.getProviderByMethod(methodName);
  }

  private getProviderByActiveTerminal(): PAYMENT_PROVIDERS {
    const activeTerminal: IActiveTerminal = this.localStorage.getObject(StorageKeys.activeTerminal);
    if (!activeTerminal.title) {
      return null;
    }
    const paymentProvider = this.getProviderByMethod(activeTerminal.title);
    if (paymentProvider === PAYMENT_PROVIDERS.PAYMASH_PAY) {
      return paymentProvider;
    }
    return null;
  }

  private isPaymentMethod(method: string, paymentMethodName: string): boolean {
    return method.indexOf(paymentMethodName) !== -1;
  }

  public splitInvoice(invoice: Invoice): ISplitInvoices {
    this.logger.debug('Start splitting invoice', { invoiceUuid: invoice?.uuid });
    const { first, second } = SplitInvoiceTypes;
    let firstInvoice: Invoice = new Invoice(invoice);

    const depositMap: Record<string, string> = {};

    let { invoiceEntries } = firstInvoice;
    invoiceEntries = this.multipleGuestsService.calculateSplitInvoiceWithGuests(invoiceEntries);
    const secondInvoice = this.createInvoice();
    firstInvoice.invoiceEntries = [];
    for (let i = 0; i < invoiceEntries.length; i++) {
      const currentEntry: InvoiceEntry = invoiceEntries[i];

      if (currentEntry.deleted) {
        firstInvoice.invoiceEntries.push(this.adjustInvoiceEntryForSplit(currentEntry, first, true));
        continue;
      }

      if (currentEntry?.quantity === 0) {
        const oldUuid: string = !!currentEntry.assignedUuid ? currentEntry?.uuid : null;
        firstInvoice.invoiceEntries.push(this.adjustInvoiceEntryForSplit(currentEntry, first, true));

        const secondInvoiceEntry: InvoiceEntry = this.adjustInvoiceEntryForSplit(currentEntry, second);
        secondInvoice.invoiceEntries.push(secondInvoiceEntry);

        if (!!oldUuid) {
          depositMap[oldUuid] = secondInvoiceEntry.uuid;
        }

        continue;
      }

      if (currentEntry.quantityForSubtract === 0) {
        firstInvoice.invoiceEntries.push(this.adjustInvoiceEntryForSplit(currentEntry, first));
        continue;
      }

      const oldUuid: string = !!currentEntry.assignedUuid ? currentEntry?.uuid : null;
      firstInvoice.invoiceEntries.push(this.adjustInvoiceEntryForSplit(currentEntry, first));

      const secondInvoiceEntry: InvoiceEntry = this.adjustInvoiceEntryForSplit(currentEntry, second);
      secondInvoice.invoiceEntries.push(secondInvoiceEntry);

      if (!!oldUuid) {
        depositMap[oldUuid] = secondInvoiceEntry.uuid;
      }
    }

    void this.logCreatedInvoiceEntries('split', secondInvoice.uuid, invoice?.uuid, secondInvoice.invoiceEntries);

    if (secondInvoice.invoiceEntries.length > 0) {
      secondInvoice.store = invoice.store;
      secondInvoice.employee = invoice.employee;
      secondInvoice.customer = invoice.customer;
      secondInvoice.customerDiscount = invoice.customerDiscount;
      secondInvoice.customerDiscountPercentage = invoice.customerDiscountPercentage;
      secondInvoice.gastronomyTable = invoice.gastronomyTable;
      secondInvoice.gastronomyTableName = invoice.gastronomyTableName;
    }

    if (Object.keys(depositMap).length > 0) {
      secondInvoice.invoiceEntries.forEach((invoice: InvoiceEntry): void => {
        if (depositMap[invoice.assignedUuid]) {
          invoice.assignedUuid = depositMap[invoice.assignedUuid];
        }
      });
    }

    if (firstInvoice?.isSelfOrder) {
      firstInvoice = this.mutateSelfOrderInvoiceToPosInvoice(firstInvoice);
    }

    firstInvoice && this.calculateInvoiceAmountAfterDiscount(firstInvoice);
    secondInvoice && this.calculateInvoiceAmountAfterDiscount(secondInvoice);

    this.logger.info('Invoice was split on to firstInvoice and secondInvoice', {
      invoiceUuid: invoice?.uuid,
      firstInvoiceUuid: firstInvoice?.uuid,
      secondInvoiceUuid: secondInvoice?.uuid,
    });

    return {
      firstInvoice,
      secondInvoice,
    };
  }

  public async splitInvoicesByGuest(invoice: Invoice, guestNumber: number): Promise<ISplitInvoices> {
    try {
      this.logger.debug('Start splitting invoice by guest', { invoiceUuid: invoice?.uuid, guestNumber });
      const newInvoice = new Invoice(invoice);
      newInvoice.invoiceEntries.forEach((invoiceEntry) => {
        if (invoiceEntry.guestNumber === guestNumber) {
          invoiceEntry.quantityForSubtract = invoiceEntry.quantity;
          invoiceEntry.quantity = 0;
        }
      });

      const { firstInvoice, secondInvoice } = this.splitInvoice(newInvoice);
      firstInvoice.invoiceEntries = this.multipleGuestsService.updateGuestNumbers(firstInvoice.invoiceEntries, guestNumber);
      secondInvoice.invoiceEntries = this.multipleGuestsService.updateGuestNumbers(secondInvoice.invoiceEntries, guestNumber);

      secondInvoice.customer = null;
      secondInvoice.isSeparated = false;
      secondInvoice.gastronomyTable = null;
      secondInvoice.gastronomyTableName = null;

      this.logger.info('Invoice was split by guestNumber on firstInvoice and secondInvoice', {
        invoiceUuid: invoice?.uuid,
        guestNumber,
        firstInvoiceUuid: firstInvoice?.uuid,
        secondInvoiceUuid: secondInvoice?.uuid,
      });
      return {
        firstInvoice,
        secondInvoice,
      };
    } catch (error) {
      this.logger.error(error, 'splitInvoicesByGuest:saveInvoice');
      throw new Error('First invoice save error');
    }
  }

  public checkInvoiceForReimburseItems(invoice: Invoice) {
    if (!invoice && !invoice?.invoiceEntries) return false;
    let isInvoiceHasReimburseItems: boolean = false;
    for (let i = 0; i < invoice.invoiceEntries.length; i++) {
      if (!invoice.invoiceEntries[i].deleted && invoice.invoiceEntries[i]?.quantity < 0) {
        isInvoiceHasReimburseItems = true;
      }
    }
    return isInvoiceHasReimburseItems;
  }

  public async saveInvoice(invoice: Invoice, withoutSync?: boolean): Promise<Invoice> {
    invoice.localModificationDate = moment.utc().toISOString();
    const data = await this.DbDaoService.upsertDataToCollection(UPDATES_TYPES.Invoice.type, [invoice]);
    const newInvoiceData = new Invoice(data['data'][0]);
    if (!withoutSync) {
      this.SyncService.addDataToSyncList(new SyncListItem(newInvoiceData, UPDATES_TYPES.Invoice.type)).catch((err) =>
        this.logger.error(err, 'saveInvoice:SyncService:addDataToSyncLis')
      );
    }
    return newInvoiceData;
  }

  public getInvoiceId(): string {
    let loggedUserData: any = this.SecurityService.getLoggedUserData();

    if (Boolean(loggedUserData)) {
      let invoiceId: string = loggedUserData.pos?.channelId + '-' + this.SecurityService.getNextInvoiceNumber();
      this.SecurityService.increaseNextInvoiceNumber();
      this.logger.info('New invoice id has been generated', { invoiceId });
      return invoiceId;
    }
  }

  public getInvoiceEntriesForKitchen(invoiceEntries: InvoiceEntry[], guestNumber: number = 0): InvoiceEntry[] {
    return invoiceEntries.filter((invoiceEntry: InvoiceEntry): boolean => {
      // A tipping entry is created with quantityForKitchenReceipt = 1 like any other entry, but
      // it is not something the kitchen prepares. Without this guard it reaches the kitchen
      // receipt (and, when all dishes were already fired, produces a ticket with the tip alone).
      if (invoiceEntry.isTipping || invoiceEntry.type === PRODUCT_TYPES.TIPS) {
        return false;
      }
      if (invoiceEntry.quantityForKitchenReceipt > 0 || invoiceEntry.isGuest) {
        const isNotDeletedEntry: boolean = !invoiceEntry?.deleted;
        const isEqualGuestNumbers = guestNumber && guestNumber === invoiceEntry.guestNumber;
        return isNotDeletedEntry && (!guestNumber || isEqualGuestNumbers);
      }
    });
  }

  private mutateSelfOrderInvoiceToPosInvoice(invoiceForMutate: Invoice): Invoice {
    const newPosInvoice: Invoice = this.createInvoice();
    this.logger.info('mutateSelfOrderInvoiceToPosInvoice start...', {
      selfOrderInvoiceForMutate: invoiceForMutate.uuid,
      newPosInvoice: newPosInvoice.uuid,
    });

    newPosInvoice.invoiceEntries = [...(invoiceForMutate?.invoiceEntries || [])];

    if (newPosInvoice.invoiceEntries.length > 0) {
      newPosInvoice.store = invoiceForMutate.store;
      newPosInvoice.employee = invoiceForMutate?.employee ? invoiceForMutate.employee : newPosInvoice.employee;
      newPosInvoice.customer = invoiceForMutate?.customer;
      newPosInvoice.customerDiscount = invoiceForMutate?.customerDiscount;
      newPosInvoice.customerDiscountPercentage = invoiceForMutate?.customerDiscountPercentage;
      newPosInvoice.gastronomyTable = invoiceForMutate?.gastronomyTable;
      newPosInvoice.gastronomyTableName = invoiceForMutate?.gastronomyTableName;
    }

    this.deleteInvoice(invoiceForMutate)
      .then(() => {
        this.logger.debug(`mutateSelfOrderInvoiceToPosInvoice: Removed self-order invoice ${invoiceForMutate.uuid} success!`);
      })
      .catch((error) => {
        this.logger.error({ error }, `mutateSelfOrderInvoiceToPosInvoice: Removed self-order invoice ${invoiceForMutate.uuid} error!`);
      });

    this.logger.debug(`mutateSelfOrderInvoiceToPosInvoice done. Return new pos invoice ${newPosInvoice.uuid}`);
    return newPosInvoice;
  }

  private adjustInvoiceEntryForSplit(invoiceEntry: InvoiceEntry, invoiceType: SplitInvoiceTypes, deleted: boolean = false): InvoiceEntry {
    const newInvoiceEntry = new InvoiceEntry(invoiceEntry);
    newInvoiceEntry.increaseLocalModificationDate();

    let { quantityForKitchenReceipt } = invoiceEntry;
    const subQuantity = invoiceType === SplitInvoiceTypes.second ? invoiceEntry?.quantity : invoiceEntry.quantityForSubtract;
    quantityForKitchenReceipt = quantityForKitchenReceipt - subQuantity;
    newInvoiceEntry.quantityForKitchenReceipt = quantityForKitchenReceipt > 0 ? quantityForKitchenReceipt : 0;

    if (invoiceType === SplitInvoiceTypes.second) {
      newInvoiceEntry.quantity = newInvoiceEntry.quantityForSubtract;
      newInvoiceEntry.uuid = this.UuidService.generate();
    }

    if (deleted) {
      newInvoiceEntry.quantityForKitchenReceipt = 0;
    }

    newInvoiceEntry.quantityForSubtract = 0;
    newInvoiceEntry.deleted = deleted;
    return newInvoiceEntry;
  }

  // Cloned entries carry only the variant uuid, so the product uuid is resolved from the local ProductVariant table.
  private async logCreatedInvoiceEntries(
    source: 'split' | 'combine' | 'copy',
    invoiceUuid: string,
    sourceInvoiceUuid: string,
    entries: InvoiceEntry[]
  ): Promise<void> {
    // Snapshot before the first await: callers keep mutating these entries.
    const createdEntries = entries.map((entry: InvoiceEntry) => ({
      invoiceEntryUuid: entry.uuid,
      entryType: entry.type,
      quantity: entry.quantity,
      guestNumber: entry.guestNumber,
      // Guest names may be customer names.
      productName: entry.isGuest ? null : entry.name,
      productUuid: entry.product?.uuid ?? null,
      productVariantUuid: entry.productVariant?.uuid ?? null,
    }));

    let variants: ProductVariant[] = [];
    const variantUuids: string[] = createdEntries.map(({ productVariantUuid }) => productVariantUuid).filter(Boolean);
    if (variantUuids.length > 0) {
      try {
        variants = await this.DbDaoService.getAllDataRaw<ProductVariant>(UPDATES_TYPES.ProductVariant.type, { uuid: oneOf(...variantUuids) });
      } catch (err) {
        this.logger.error(err, 'logCreatedInvoiceEntries:getAllDataRaw');
      }
    }

    createdEntries.forEach((createdEntry) => {
      const variant: ProductVariant = variants.find(({ uuid }) => uuid === createdEntry.productVariantUuid);
      this.logger.info(`Invoice entry has been created after: ${source} invoice`, {
        invoiceUuid,
        sourceInvoiceUuid,
        ...createdEntry,
        productUuid: createdEntry.productUuid ?? variant?.productUuid ?? null,
      });
    });
  }

  public getEntryByUuidAndCategory(type: string, data: any, invoiceEntries: InvoiceEntry[], isCancelProcess: boolean = false): number[] {
    let foundValues: number[] = [];

    const regularTypes: string[] = [PRODUCT_TYPES.PRODUCT, PRODUCT_TYPES.TIPS, PRODUCT_TYPES.SERVICE, PRODUCT_TYPES.DEPOSIT_RETURN];
    const cancelProcessTypes: string[] = [PRODUCT_TYPES.DEPOSIT];

    const productTypes = isCancelProcess ? [...cancelProcessTypes, ...regularTypes] : [...regularTypes];

    if (productTypes.includes(type)) {
      const uuidToFound = data.variant?.uuid || data.productVariant?.uuid;
      let newCategory = data.productCategory ? (data.productCategory.uuid ? data.productCategory.uuid : data.productCategory) : null;
      if (newCategory === 'root') newCategory = null;

      for (let i = 0; i < invoiceEntries.length; i++) {
        const current = invoiceEntries[i];
        if (!current.deleted && productTypes.includes(current?.type)) {
          let currentCategory = current.productCategory ? current.productCategory['uuid'] : null;
          if (currentCategory === 'root') currentCategory = null;
          if (currentCategory === newCategory) {
            data = new InvoiceEntry(data);
            const isEqualGuestNumber = data.guestNumber === current.guestNumber;
            if (data.hasOwnProperty('position') && data.position === current.position && data.uuid === current.uuid && isEqualGuestNumber) {
              foundValues = [...foundValues, i];
            } else if (current.discountedPrice === data.discountedPrice) {
              if (current.productVariant?.['uuid'] === uuidToFound && current?.note === data?.note && isEqualGuestNumber) {
                foundValues = [...foundValues, i];
              } else if (current.uuid && data.uuid && current.uuid === data.uuid && current.note === data.note && isEqualGuestNumber) {
                foundValues = [...foundValues, i];
              }
            }
          }
        }
      }
    } else if (type === PRODUCT_TYPES.INDIVIDUAL) {
      for (let i = 0; i < invoiceEntries.length; i++) {
        const current = invoiceEntries[i];
        const isIndividualProduct = current?.type === PRODUCT_TYPES.INDIVIDUAL && !current.deleted;
        const isEqualProductUuid = data.product && current.product && data.product.uuid === current.product.uuid;
        if (isIndividualProduct && (current.uuid === data.uuid || isEqualProductUuid)) {
          foundValues = [...foundValues, i];
        }
      }
    } else if (type === PRODUCT_TYPES.DEPOSIT) {
      for (let i = 0; i < invoiceEntries.length; i++) {
        const current = invoiceEntries[i];
        const isDeposit = current?.type === PRODUCT_TYPES.DEPOSIT && !current?.deleted;
        const isEqualDeposit = current.assignedUuid === data.assignedUuid;
        if (isDeposit && isEqualDeposit) {
          foundValues = [...foundValues, i];
        }
      }
    }
    return foundValues;
  }

  public mergeInvoiceEntries(invoiceEntries: Array<InvoiceEntry>): Array<InvoiceEntry> {
    let mergedInvoiceEntries: Array<InvoiceEntry> = [];
    let duplicatedIndexes: Array<Array<number>> = [];
    for (let i = 0; i < invoiceEntries.length; i++) {
      let currentEntryIndexes: Array<number> = this.getEntryByUuidAndCategory(invoiceEntries[i]?.type, invoiceEntries[i], invoiceEntries);
      if (currentEntryIndexes.length > 1 && !this.checkForExistingItems(duplicatedIndexes, currentEntryIndexes)) {
        duplicatedIndexes.push(currentEntryIndexes);
      }
      if (currentEntryIndexes.length < 2) {
        mergedInvoiceEntries.push(invoiceEntries[i]);
      }
    }
    for (let j = 0; j < duplicatedIndexes.length; j++) {
      mergedInvoiceEntries.push(this.mergeDuplicatedEntries(duplicatedIndexes[j], invoiceEntries));
    }
    return mergedInvoiceEntries;
  }

  private checkForExistingItems(duplicatedIndexes: Array<Array<number>>, currentEntryIndexes: Array<number>): boolean {
    for (let i = 0; i < duplicatedIndexes.length; i++) {
      let sortFunction = (a, b) => {
        return a - b;
      };
      duplicatedIndexes[i].sort(sortFunction);
      currentEntryIndexes.sort(sortFunction);
      if (duplicatedIndexes[i].toString().indexOf(currentEntryIndexes.toString()) !== -1) return true;
    }
    return false;
  }

  private mergeDuplicatedEntries(duplicatedIndexes: Array<number>, invoiceEntries: Array<InvoiceEntry>): InvoiceEntry {
    let mergedInvoiceEntry: InvoiceEntry;
    for (let i = 0; i < duplicatedIndexes.length; i++) {
      if (!mergedInvoiceEntry) {
        mergedInvoiceEntry = invoiceEntries[duplicatedIndexes[i]];
      } else {
        mergedInvoiceEntry.totalAmount += invoiceEntries[duplicatedIndexes[i]].totalAmount;
        mergedInvoiceEntry.quantity += invoiceEntries[duplicatedIndexes[i]].quantity;
        mergedInvoiceEntry.quantityForKitchenReceipt += invoiceEntries[duplicatedIndexes[i]].quantityForKitchenReceipt;
      }
    }
    return mergedInvoiceEntry;
  }

  async cancelInvoice(invoice: Invoice, paymentMethod: string, cancellationInvoiceParams: ICancellationInvoiceParams): Promise<Invoice> {
    this.logger.debug('Start cancelling invoice by paymentMethod', { invoiceUuid: invoice.uuid, paymentMethod });
    const employee: Employee = this.facadeEmployeeState.activeEmployeeSnapshot;
    const { cancellationReason = '', restockOnCancellation, refundToGiftCardIdentifier, cancellationType } = cancellationInvoiceParams;
    invoice.cancellationReason = cancellationReason;
    invoice.restockOnCancellation = restockOnCancellation;
    invoice.cancellingEmployee = employee;

    const invoicePartiallyCancel = new InvoicePartiallyCancel(invoice, paymentMethod);
    invoicePartiallyCancel.setInvoiceEntries(invoice.invoiceEntries);

    const data = await this.DbDaoService.getAllData(UPDATES_TYPES.PaymentMethod.type, { method: paymentMethod });
    const selectedPaymentMethod = new PaymentMethod(data.data[0] || {});
    const paymentProvider = this.getProviderByMethodWithSupportedTwint(selectedPaymentMethod?.name);
    const supportedPaymentProviders = [
      PAYMENT_PROVIDERS.MYPOS,
      PAYMENT_PROVIDERS.MYPOSGLASS,
      PAYMENT_PROVIDERS.PAYMASH_PAY,
      PAYMENT_PROVIDERS.SOFTPOS,
    ];

    let refundMethodName: string | undefined;
    if (supportedPaymentProviders.includes(paymentProvider)) {
      const currency = this.SecurityService.getLoggedCompanyData()?.locale?.currency || '';
      refundMethodName = await this.cancelInvoiceOnTerminal(
        invoice.amount,
        currency,
        selectedPaymentMethod?.name,
        invoice.uuid,
        this.UuidService.generate(),
        paymentProvider
      );
    } else if (paymentProvider) {
      throw PaymentError.REFUND_NOT_SUPPORTED;
    } else if (selectedPaymentMethod.method === DefaultPaymentMethods.GIFTCARD) {
      invoicePartiallyCancel.refundToGiftCardIdentifier = refundToGiftCardIdentifier;
    }

    if (refundMethodName) {
      invoicePartiallyCancel.refundPaymentMethod = refundMethodName;
    }
    this.logger.info('Invoice will send to server', { invoiceUuid: invoice.uuid, invoicePartiallyCancel });

    const cancelledInvoice = cancellationType === CancellationTypes.full
      ? await this.invoicesApiService.upsertCancelFully(invoicePartiallyCancel)
      : await this.invoicesApiService.upsertCancelPartially(invoicePartiallyCancel);

    this.logger.info('Invoice was successfully canceled', { invoiceUuid: cancelledInvoice.uuid });
    return this.saveInvoice(cancelledInvoice, true);
  }

  private async cancelInvoiceOnTerminal(
    amount: number,
    currency: string,
    paymentMethod: string,
    invoiceUuid: string,
    paymentUuid: string,
    paymentProvider: PAYMENT_PROVIDERS
  ): Promise<string> {
    const paymentResult = await this.PaymentService.makeTransaction({
      paymentProvider,
      amount,
      currency,
      invoiceUuid,
      paymentMethod,
      paymentUuid,
      transactionType: PAYMENT_TRANSACTION.PAYMENT_REFUND,
    });
    return this.getPaymentMethodByPaymentType(paymentResult.cardName, paymentMethod);
  }

  public async removeOldInvoicesFromLocalDb(): Promise<void> {
    if (this.oldInvoicesRemovalInProgress) {
      this.logger.debug('removeOldInvoicesFromLocalDb: already in progress, skipped');
      return;
    }
    this.oldInvoicesRemovalInProgress = true;

    const duration = this.posSettingsService.getInvoicesKeepDuration();
    const cutoffDate = moment.utc().subtract(duration, 'day').startOf('day').toISOString();
    const queryParams = query(
      {
        isDraft: false,
        sequentId: greaterThan(0),
        date: lessOrEqualsThan(cutoffDate),
      },
      group({ _syncState: isNull() }, or({ _syncState: SYNC_ITEM_STATES.SYNCHRONIZED }))
    );

    try {
      const matched = await this.DbDaoService.count(UPDATES_TYPES.Invoice.type, queryParams);
      await this.DbDaoService.remove(UPDATES_TYPES.Invoice.type, queryParams);
      this.logger.info('removeOldInvoicesFromLocalDb', { duration, cutoffDate, matched });
    } catch (err) {
      this.logger.error(err, 'removeOldInvoicesFromLocalDb:removeDataByParams', undefined, { duration, cutoffDate });
    } finally {
      this.oldInvoicesRemovalInProgress = false;
    }
  }

  public makeInvoiceEntryToAddData(variant: ProductVariant, product: Product, productShortName: string, productCategoryUuid: string): any {
    let productName = product?.name;
    if (productShortName?.length > 0) {
      productName += ` (${productShortName})`;
    }

    //TODO adjust image search
    return {
      productUuid: product?.uuid ?? null,
      productName: product?.name ?? null,
      variant: variant,
      price: trustValue(variant?.price) ? variant.price : 0,
      taxRate: trustValue(variant?.vatRate?.['value']) ? variant.vatRate['value'] : 0,
      productCategory: productCategoryUuid,
      name: productName,
      shortName: productShortName,
      image: null,
      wasPrice: trustValue(variant?.wasPrice) ? variant.wasPrice : 0,
    };
  }

  public addInvoiceEntryToInvoice(invoice: Invoice, newEntryData: any, entryType: string, quantity: number, guestNumber?: number): Invoice {
    this.logger.debug('Start adding invoice entry to invoice', { invoiceUuid: invoice.uuid });
    const invoiceEntry: InvoiceEntry = this.createInvoiceEntry(newEntryData, entryType, quantity, guestNumber);
    invoiceEntry.position = invoice.invoiceEntries.length;
    invoice.invoiceEntries.push(invoiceEntry);
    this.logger.info('Invoice entry has been added with quantity to invoice', {
      invoiceEntryCreated: true,
      invoiceEntryUuid: invoiceEntry.uuid,
      quantity: invoiceEntry.quantity,
      invoiceUuid: invoice.uuid,
      entryType: invoiceEntry.type,
      productName: newEntryData.productName ?? invoiceEntry.name,
      productUuid: newEntryData.productUuid ?? newEntryData.variant?.productUuid ?? null,
      productVariantUuid: invoiceEntry.productVariant?.uuid ?? null,
    });
    return new Invoice(invoice);
  }

  public createInvoiceEntry(newEntryData: any, entryType: string, quantity: number, guestNumber?: number): InvoiceEntry {
    // if product NOT exists in cart
    // push product to cart
    // set product quantity to 1 if it not already have quantity field

    let taxRate = 0;
    if (newEntryData.taxRate) {
      taxRate = newEntryData.taxRate;
    } else if (newEntryData.variant) {
      taxRate = newEntryData.variant.vatRate.value;
    }
    const employeeUuid: string = this.facadeEmployeeState.activeEmployeeSnapshot?.uuid;
    const entryToAdd: any = {
      type: entryType,
      name: newEntryData?.name,
      shortName: newEntryData.shortName,
      image: newEntryData.image,
      note: newEntryData.note,
      quantity: quantity,
      quantityForKitchenReceipt: quantity || 1,
      price: newEntryData?.price,
      taxRate: taxRate,
      discountedPrice: newEntryData.discountedPrice,
      discount: newEntryData.discount,
      discountPercentage: newEntryData.discountPercentage,
      bgColor: newEntryData.bgColor,
      isTipping: newEntryData?.isTipping,
      employee: {
        uuid: employeeUuid,
      },
      uuid: this.UuidService.generate(),
    };

    entryToAdd.store = {
      uuid: this.SecurityService.getActiveStore()?.uuid,
    };

    if (guestNumber) {
      entryToAdd.guestNumber = guestNumber;
    }

    const types = [PRODUCT_TYPES.PRODUCT, PRODUCT_TYPES.GIFT_CARD, PRODUCT_TYPES.TIPS, PRODUCT_TYPES.SERVICE];
    if (types.includes(entryType)) {
      entryToAdd.productVariant = {
        uuid: newEntryData.variant ? newEntryData.variant.uuid : newEntryData.productVariant['uuid'],
      };

      if (newEntryData.productCategory) {
        entryToAdd.productCategory = {
          uuid: newEntryData.productCategory,
        };
      } else {
        entryToAdd.productCategory = null;
      }

      entryToAdd.inventoryStore = {
        uuid: this.SecurityService.getActiveStore()?.uuid,
      };
    }
    return new InvoiceEntry(entryToAdd);
  }

  public deleteInvoice(invoiceToDelete: Invoice) {
    invoiceToDelete.deleted = true;
    this.logger.info('Draft invoice has been removed', { invoiceUuid: invoiceToDelete.uuid });
    this.facadeInvoiceService.removeDeletedSelfOrderInvoiceFromCounter(invoiceToDelete);
    return this.saveInvoice(invoiceToDelete);
  }

  public async combineInvoices(firstInvoiceUuid: string, secondInvoiceUuid: string): Promise<Invoice> {
    this.logger.debug('Start to combine invoices firstInvoiceUuid and secondInvoiceUuid', { firstInvoiceUuid, secondInvoiceUuid });

    const firstData = await this.DbDaoService.getDataByUUID(UPDATES_TYPES.Invoice.type, firstInvoiceUuid);
    const firstInvoice = new Invoice(firstData['data']);
    this.logger.debug('combineInvoices:firstInvoice', {
      firstInvoice: new InvoiceForLogsClass(ignoreImageProps(firstInvoice) as Invoice),
    });

    const secondData = await this.DbDaoService.getDataByUUID(UPDATES_TYPES.Invoice.type, secondInvoiceUuid);
    const secondInvoice = new Invoice(secondData['data']);
    this.logger.debug('combineInvoices:secondInvoice', {
      secondInvoice: new InvoiceForLogsClass(ignoreImageProps(secondInvoice) as Invoice),
    });

    firstInvoice.customer = firstInvoice.customer || secondInvoice.customer;

    let entriesToBePushed: InvoiceEntry[] = [];
    const createdEntries: InvoiceEntry[] = [];
    const uuidDuplicateDeposits: string[] = [];
    let maxGuestNumber = 0;
    const hasFirstInvoiceEntryGuests = firstInvoice.hasGuestInvoiceEntry();

    if (hasFirstInvoiceEntryGuests) {
      const guestNumbers = firstInvoice.getGuestInvoiceEntries().map((invoiceEntry) => invoiceEntry.guestNumber);
      maxGuestNumber = Math.max(...guestNumbers, maxGuestNumber);
    }

    const hasSecondInvoiceEntryGuests = secondInvoice.hasGuestInvoiceEntry();

    secondInvoice.invoiceEntries.forEach((item: InvoiceEntry): void => {
      if (hasFirstInvoiceEntryGuests && hasSecondInvoiceEntryGuests) {
        const currentGuestNumber = item.guestNumber;
        item.guestNumber += maxGuestNumber;
        if (item.isGuest) {
          item.name = this.multipleGuestsService.getGuestName(currentGuestNumber, item?.name, item?.guestNumber);
        }
      }

      const entryIndexes = this.getEntryByUuidAndCategory(item?.type, item, firstInvoice.invoiceEntries);

      if (entryIndexes.length > 0) {
        const itemForUpdate: InvoiceEntry = firstInvoice.invoiceEntries[entryIndexes[0]];

        itemForUpdate.quantity += item.quantity;
        itemForUpdate.quantityForKitchenReceipt += item.quantityForKitchenReceipt;

        if (itemForUpdate.specialTaxing !== item.specialTaxing) {
          itemForUpdate.specialTaxing = SpecialTaxing.inHouse;
        }

        if (item.assignedUuid && itemForUpdate.assignedUuid) {
          const depositEntry: InvoiceEntry = firstInvoice.invoiceEntries.find(
            (item: InvoiceEntry): boolean => item.uuid === itemForUpdate.assignedUuid
          );
          depositEntry.quantity = itemForUpdate.quantity;
          uuidDuplicateDeposits.push(item.assignedUuid);
        }
      } else {
        const entryToPush: InvoiceEntry = new InvoiceEntry(item);
        if (!entryToPush.assignedUuid || entryToPush.type !== PRODUCT_TYPES.DEPOSIT) {
          entryToPush.uuid = this.UuidService.generate();
          createdEntries.push(entryToPush);
        }
        entriesToBePushed = [...entriesToBePushed, entryToPush];
      }
    });

    void this.logCreatedInvoiceEntries('combine', firstInvoice.uuid, secondInvoiceUuid, createdEntries);

    const employeeUuid: string = this.facadeEmployeeState.activeEmployeeSnapshot?.uuid;

    firstInvoice.invoiceEntries = firstInvoice.invoiceEntries.concat(entriesToBePushed);
    firstInvoice.invoiceEntries = firstInvoice.invoiceEntries.filter(
      (item: InvoiceEntry): boolean => !uuidDuplicateDeposits.includes(item?.uuid)
    );
    firstInvoice.employee = { uuid: employeeUuid };
    firstInvoice.modificationDate = moment.utc().toISOString();

    if (firstInvoice.invoiceEntries.some((entry: InvoiceEntry): boolean => !!entry.assignedUuid)) {
      firstInvoice.invoiceEntries.forEach((invoiceEntry: InvoiceEntry, idx: number): void => {
        invoiceEntry.position = idx;
      });
    }

    if (firstInvoice.discountPercentage === 0 && firstInvoice.discount === 0) {
      firstInvoice.discountPercentage = secondInvoice.discountPercentage;
      firstInvoice.discount = secondInvoice.discount;
    }

    if (firstInvoice.customerDiscount === 0) {
      firstInvoice.customerDiscount = secondInvoice.customerDiscount;
    }

    if (firstInvoice.customerDiscountPercentage === 0 && firstInvoice.customerDiscount === 0) {
      firstInvoice.customerDiscountPercentage = secondInvoice.customerDiscountPercentage;
    }

    let mainInvoice: Invoice = firstInvoice;
    if (firstInvoice.invoiceEntries.some(({ specialTaxing }): boolean => specialTaxing === SpecialTaxing.takeAway)) {
      mainInvoice = await this.recalculateTaxRateInvoiceToInHouse(firstInvoice);
    }

    this.calculateInvoiceAmountAfterDiscount(mainInvoice);
    const [savedInvoice] = await Promise.all([
      this.saveInvoice(mainInvoice),
      this.deleteInvoice(secondInvoice),
    ]);
    this.logger.info(`Invoices: first ${firstInvoiceUuid}, second ${secondInvoiceUuid} were combined`, {
      mainInvoice: new InvoiceForLogsClass(mainInvoice as Invoice),
    });
    return savedInvoice;
  }

  private async recalculateTaxRateInvoiceToInHouse(mainInvoice: Invoice): Promise<Invoice> {
    const invoiceEntries = mainInvoice.invoiceEntries.filter(({ specialTaxing }): boolean => specialTaxing === SpecialTaxing.takeAway);
    await Promise.all(
      invoiceEntries
        .filter((entry) => !!entry.productVariant)
        .map(async (entry) => {
          const data: DbResponse = await this.DbDaoService.getDataByUUID(UPDATES_TYPES.ProductVariant.type, entry.productVariant.uuid);
          const rate = data.data?.vatRate.value;
          if (rate) {
            entry.specialTaxing = SpecialTaxing.inHouse;
            entry.taxRate = rate;
          }
        })
    );
    return mainInvoice;
  }

  public subInvoices(invoice: Invoice, subInvoices: Invoice[], isCancelProcess: boolean = false): Invoice {
    if (!subInvoices.length) {
      return invoice;
    }
    const invoiceEntries = this.multipleGuestsService.calculateSplitInvoiceWithGuests(invoice.invoiceEntries);
    const subInvoiceEntries = [].concat(...subInvoices.map((invoice) => invoice.invoiceEntries));
    subInvoiceEntries.forEach((subInvoiceEntry) => {
      const entryIndexes = this.getEntryByUuidAndCategory(subInvoiceEntry.type, subInvoiceEntry, invoiceEntries, isCancelProcess);
      const currentEntry = invoiceEntries[entryIndexes[0]];

      if (currentEntry) {
        currentEntry.quantity -= subInvoiceEntry.quantity;
        const guestInvoiceEntry = invoice.getGuestInvoiceEntryByGuestNumber(currentEntry.guestNumber);
        if (guestInvoiceEntry) {
          guestInvoiceEntry.quantity -= subInvoiceEntry.quantity;
        }
      }
    });
    this.calculateInvoiceAmountAfterDiscount(invoice);
    return invoice;
  }

  public isPartiallyCancelled(invoice: Invoice, cancellationInvoices: Invoice[]): boolean {
    if (invoice.invoiceType !== INVOICE_TYPES.INVOICE || !cancellationInvoices.length) {
      return false;
    }

    const newInvoice = new Invoice(invoice);
    if (newInvoice?.isUnpaidInvoiceWithOnInvoicePaymentMethod()) {
      newInvoice.invoiceEntries = newInvoice.getActiveInvoiceEntries().filter((entry) => entry.type !== PRODUCT_TYPES.GIFT_CARD);
    }

    let totalProductsCount = this.getTotalProductsCountByInvoiceEntries(newInvoice.invoiceEntries);
    cancellationInvoices
      .filter(
        (cancellationInvoice) =>
          cancellationInvoice.originalInvoiceReference && cancellationInvoice.originalInvoiceReference.uuid === newInvoice.uuid
      )
      .map((cancellationInvoice) => {
        totalProductsCount -= this.getTotalProductsCountByInvoiceEntries(cancellationInvoice.invoiceEntries);
      });
    return totalProductsCount !== 0;
  }

  private getTotalProductsCountByInvoiceEntries(invoiceEntries: InvoiceEntry[]): number {
    return invoiceEntries.reduce((totalProductsCount, entry) => {
      if (!entry.deleted && entry.type !== PRODUCT_TYPES.CATEGORY) {
        totalProductsCount += Math.abs(entry.quantity);
      }
      return totalProductsCount;
    }, 0);
  }

  public mergeUpdateAndActiveInvoiceEntries(
    invoiceEntries: InvoiceEntry[],
    activeInvoiceEntries: InvoiceEntry[]
  ): IMergeUpdateAndActiveInvoiceEntries {
    let saveRequired = false;
    // Reconcile incoming entries, preferring the local copy when it is newer.
    const entries = invoiceEntries.map((invoiceEntry) => {
      const activeInvoiceEntry = activeInvoiceEntries.find((i) => i.uuid === invoiceEntry.uuid);
      if (this.isChangedActiveInvoiceEntry(invoiceEntry, activeInvoiceEntry)) {
        saveRequired = true;
        return activeInvoiceEntry;
      }
      return invoiceEntry;
    });

    // Keep locally-added entries the remote snapshot does not yet contain (e.g. rapid
    // taps added after the snapshot was taken). Treated as un-synced local additions,
    // consistent with the local-wins reconciliation above — never silently dropped.
    const remoteUuids = new Set(invoiceEntries.map((entry) => entry.uuid));
    const localOnlyEntries = activeInvoiceEntries.filter(
      (entry) => entry?.uuid && !entry.deleted && !remoteUuids.has(entry.uuid)
    );
    if (localOnlyEntries.length) {
      saveRequired = true;
    }

    return {
      invoiceEntries: [...entries, ...localOnlyEntries],
      isSaveRequired: saveRequired,
    };
  }

  private isChangedActiveInvoiceEntry(invoiceEntry: InvoiceEntry, activeInvoiceEntry: InvoiceEntry) {
    return activeInvoiceEntry && invoiceEntry.localModificationDate < activeInvoiceEntry.localModificationDate;
  }

  public async copyPaidInvoiceConfirm(invoice: Invoice) {
    const alert: HTMLIonAlertElement = await this.alertService.create({
      message: this.translateService.instant('invoice-copy.confirm-popover.msg'),
      header: this.translateService.instant('invoice-copy.confirm-popover.header'),
      subHeader: null,
      buttons: [
        {
          text: this.translateService.instant('invoice-copy.confirm-popover.btn.title'),
          handler: (): void => {
            this.copyPaidInvoice(invoice)
              .catch((err: Error) => { this.logger.error(err, 'Copy invoice failure'); });
          },
        },
        {
          text: this.translateService.instant('common_cancel'),
          role: 'cancel',
        },
      ],
    });
    alert.present();
  }

  /**
   * Create a new draft invoice based on a paid invoice.
   * - Copies invoice entries (products/services/tips/shipping/etc.), quantities, prices, notes, discounts, customer and table.
   * - Does NOT copy invoicePayments or paymentMethod.
   * - Fails if the source invoice contains a gift card entry.
   */
  public async copyPaidInvoice(originalInvoice: Invoice): Promise<Invoice | undefined> {
    if (!originalInvoice) {
      this.logger.warning('Original invoice is required');
      return;
    }

    if (!originalInvoice.isPaid) {
      this.logger.warning('Only paid invoices can be copied');
      return;
    }

    const containsGiftCard: boolean = (originalInvoice.invoiceEntries || [])
      .some((e: InvoiceEntry): boolean => !e?.deleted && e?.type === PRODUCT_TYPES.GIFT_CARD);

    if (containsGiftCard) {
      this.logger.warning('Cannot clone invoice containing gift card');
    }

    // Create base draft invoice
    const newInvoice: Invoice = this.createInvoice();
    const isMultipleGuests: boolean = this.multipleGuestsService.isMultipleGuests;
    const originalInvoiceWithGuest: boolean = originalInvoice.hasGuestInvoiceEntry();
    const guestEntriesHaveToRemove: boolean = !isMultipleGuests && originalInvoiceWithGuest;
    const guestHaveToBeAdded: boolean = isMultipleGuests && !originalInvoiceWithGuest;

    // Copy invoice-level metadata
    newInvoice.isNet = originalInvoice?.isNet;
    newInvoice.customer = originalInvoice?.customer ? { ...originalInvoice.customer } : null;
    newInvoice.gastronomyTable = originalInvoice?.gastronomyTable ? { ...originalInvoice.gastronomyTable } : null;
    newInvoice.gastronomyTableName = originalInvoice?.gastronomyTableName || null;

    newInvoice.discount = originalInvoice?.discount || 0;
    newInvoice.discountPercentage = originalInvoice?.discountPercentage || 0;
    newInvoice.customerDiscount = originalInvoice?.customerDiscount || 0;
    newInvoice.customerDiscountPercentage = originalInvoice?.customerDiscountPercentage || 0;
    newInvoice.customerNote = originalInvoice?.customerNote || null;
    newInvoice.discountCode = originalInvoice?.discountCode || null;
    newInvoice.discountCodeDiscount = originalInvoice?.discountCodeDiscount || 0;
    newInvoice.discountPercentage = originalInvoice?.discountPercentage || 0;

    // Copy invoice entries (active ones) with new uuids and preserve deposit mapping
    const entries: InvoiceEntry[] = ([ ...originalInvoice?.invoiceEntries ] || []).filter((e: InvoiceEntry): boolean => !e.deleted);
    // const uuidMap: Record<string, string> = {};
    let clonedEntries: InvoiceEntry[] = entries.map((entry: InvoiceEntry): InvoiceEntry => {
      const cloned: InvoiceEntry = new InvoiceEntry(entry);
      // Assign new UUID for every entry (fresh invoice)
      const newUuid: string = this.UuidService.generate();
      // uuidMap[entry.uuid] = newUuid;
      cloned.uuid = newUuid;
      // Reset kitchen receipt counter to full quantity for new invoice
      cloned.quantityForKitchenReceipt = Math.abs(cloned.quantity);
      // Position will be reindexed later
      cloned.position = null;
      // Clean any link that must not be carried over across invoices, it will be remapped below
      return cloned;
    });

    if (guestEntriesHaveToRemove) {
      clonedEntries = [ ...clonedEntries].filter((item: InvoiceEntry): boolean => !item.isGuest);
    }

    if (guestHaveToBeAdded) {
      const firstGuestNumber: number = 1;
      const firstGuestName: string = `${this.translateService.instant('common_guest')} ${firstGuestNumber}`;
      const firstEntryGuest: InvoiceEntry = new InvoiceEntry({
        uuid: this.UuidService.generate(),
        type: PRODUCT_TYPES.CATEGORY,
        name: firstGuestName,
        guestNumber: firstGuestNumber,
        quantity: 1,
        quantityForKitchenReceipt: 1,
        employee: { uuid: this.facadeEmployeeState.activeEmployeeSnapshot?.uuid },
      });
      firstEntryGuest.setLocalCreationDate();
      clonedEntries.unshift(firstEntryGuest);
    }

    if (isMultipleGuests) {
      clonedEntries = this.multipleGuestsService.recalculateGuestNumbersForCopyInvoices(clonedEntries);
    }

    clonedEntries.forEach((entry: InvoiceEntry, idx: number): number => (entry.position = idx));

    newInvoice.invoiceEntries = [...clonedEntries];
    void this.logCreatedInvoiceEntries('copy', newInvoice.uuid, originalInvoice.uuid, clonedEntries);

    // Ensure payments are not copied
    newInvoice.invoicePayments = [];
    newInvoice.paymentMethod = null;
    newInvoice.isPaid = false;
    newInvoice.isPartiallyPaid = false;

    // Calculate totals and save
    this.calculateInvoiceAmountAfterDiscount(newInvoice);
    const cartService: CartService = this.inject.get(CartService);

    try {
      const value: Invoice = await this.saveInvoice(newInvoice);
      this.logger.info('Invoice has been copied', {
        originalInvoiceUuid: originalInvoice?.uuid,
        copiedInvoiceUuid: value?.uuid,
      });
      try {
        await this.navController.navigateBack(ROUTE_URLS.collection, { animated: true, animationDirection: 'forward' });
        const activeCopy: Invoice = new Invoice(value);
        cartService.setActiveInvoice(activeCopy);
        // Move the customer display off any success/email/invoiceSent screen onto the new draft.
        // Copy can be triggered from several places (success screen, checkout invoice details,
        // invoice lists), so the transition is centralised here rather than per-caller. The display
        // exits those states only on an explicit processInvoiceChange; the two-step reset
        // (undefined -> copy) forces it to leave the prior screen before showing the copy, and the
        // gap lets the reset render on the device first (aligned with the display's cart auditTime).
        const customerDisplayService: ICustomerDisplayService = this.inject.get(ICustomerDisplayService);
        await customerDisplayService.processInvoiceChange(undefined);
        await new Promise((resolve) => setTimeout(resolve, CUSTOMER_DISPLAY_COPY_RESET_DELAY_MS));
        await customerDisplayService.processInvoiceChange(activeCopy);
        return activeCopy;
      } catch (error) {
        this.logger.error(error as Error, 'Error while navigating back to collection');
      }
    } catch (error) {
      this.logger.error(error as Error, 'Error while saving copy paid invoice', {}, { uuid: newInvoice.uuid });
    }
    return undefined;
  }

  // TODO Look like deprecated method
  updateBrokenInvoicesWithDeletePayments() {
    const queryParams = query({
      isDraft: true,
      invoicePayments: arrayIsNotEmpty(),
    });
    return this.DbDaoService.getAllData(UPDATES_TYPES.Invoice.type, queryParams)
      .then((data) => {
        for (let i = 0; i < data.data.length; i++) {
          const invoice = new Invoice(data.data[i]);
          const invoicePaymentsTotal = invoice.getPaymentsTotal();
          if (invoicePaymentsTotal >= invoice.amount) {
            this.logger.info('Invoice is broken and should be updated and synchronized', { invoiceUuid: invoice.uuid });
            invoice.invoiceId = invoice.invoiceId || this.getInvoiceId();
            invoice.date = invoice.date || invoice.localCreationDate;
            invoice.setInvoiceAsPaid();
            this.saveInvoice(invoice).catch((err) => this.logger.error(err, 'repeatSyncForInvoices:saveInvoice'));
          }
        }
      })
      .catch((err) => {
        this.logger.error(err, 'updateBrokenInvoices:getDataByParams');
        throw err;
      });
  }

  updatePaidInvoicesWithoutSequentId() {
    const queryParams = query({
      isPaid: true,
      isDraft: false,
      sequentId: isNull(),
    });
    return this.DbDaoService.getAllData(UPDATES_TYPES.Invoice.type, queryParams)
      .then((data) => {
        for (let i = 0; i < data.data.length; i++) {
          const invoice = new Invoice(data.data[i]);
          this.logger.info('Invoice has not sequentId and should be updated and synchronized', { invoiceUuid: invoice.uuid });
          this.saveInvoice(invoice).catch((err) => this.logger.error(err, 'repeatSyncForInvoices:saveInvoice'));
        }
      })
      .catch((err) => {
        this.logger.error(err, 'updateBrokenInvoices:getDataByParams');
        throw err;
      });
  }

  calculateInvoiceAmountAfterDiscount(invoice: Invoice, currentCompanyCurrencyRounding: string = '') {
    currentCompanyCurrencyRounding = this.getCurrentCompanyCurrencyRounding(currentCompanyCurrencyRounding);
    invoice.calculateInvoiceAmountAfterDiscount(currentCompanyCurrencyRounding);
  }

  calculateCancellationInvoiceAmountAfterDiscount(
    invoice: Invoice,
    originInvoice: Invoice = null,
    currentCompanyCurrencyRounding: string = ''
  ) {
    currentCompanyCurrencyRounding = this.getCurrentCompanyCurrencyRounding(currentCompanyCurrencyRounding);
    let originAmountInvoice = originInvoice ? originInvoice.calculateAmountBeforeDiscount() : 0;
    originAmountInvoice = MathUtils.roundByFinalRoundingSize(originAmountInvoice, LOCALE.CurrencyRounding[currentCompanyCurrencyRounding]);
    invoice.calculateInvoiceAmountAfterDiscount(currentCompanyCurrencyRounding, originAmountInvoice);
  }

  private getCurrentCompanyCurrencyRounding(currentCompanyCurrencyRounding: string = '') {
    if (!currentCompanyCurrencyRounding) {
      const currentCompany: Company = this.SecurityService.getLoggedCompanyData();
      return currentCompany.locale.currencyRounding;
    }
    return currentCompanyCurrencyRounding;
  }

  getTaxes(invoice: Invoice): InvoiceDetailsTaxItem[] {
    const taxes: InvoiceDetailsTaxItem[] = [];
    const invoiceEntries = invoice.getActiveInvoiceEntriesWithoutTypes(PRODUCT_TYPES.CATEGORY, PRODUCT_TYPES.SHIPPING_COST);
    let groupInvoiceEntries = this.getGroupInvoiceEntries(invoiceEntries);

    for (const entry of groupInvoiceEntries) {
      let { totalAmount } = entry;
      const discountedPrice = totalAmount;

      if (invoice.hasDiscountPercentage()) {
        const discountPercentage = MathUtils.roundHalfUp(totalAmount * (invoice.discountPercentage / 100), 2);
        totalAmount = MathUtils.normalizeSubtraction(totalAmount, discountPercentage);
      }

      if (invoice.hasCustomerDiscountPercentage()) {
        const customerDiscountPercentage = MathUtils.roundHalfUp(totalAmount * (invoice.customerDiscountPercentage / 100), 2);
        totalAmount = MathUtils.normalizeSubtraction(totalAmount, customerDiscountPercentage);
      }

      if (invoice.discountCode?.type === DISCOUNT_CODE_TYPES.PERCENTAGE) {
        const discountPercentage = MathUtils.roundHalfUp(totalAmount * (invoice.discountCode.value / 100), 2);
        totalAmount = MathUtils.normalizeSubtraction(totalAmount, discountPercentage);
      }

      if (invoice.hasDiscount() || invoice.hasCustomerDiscount() || invoice.discountCode?.type === DISCOUNT_CODE_TYPES.FIXED) {
        const amountWithoutShippingCost = invoice.getAmountWithoutShippingCost(invoice.totalAmountWithoutDiscount);
        const totalDiscount = invoice.discount + invoice.customerDiscount + invoice.discountCodeDiscount;
        const discount = (discountedPrice / amountWithoutShippingCost) * totalDiscount;
        totalAmount = MathUtils.normalizeSubtraction(totalAmount, discount);
      }
      entry.totalAmount = totalAmount;
    }

    const shippingCost = invoice.getShippingCostInvoiceEntry();
    if (shippingCost) {
      groupInvoiceEntries = this.getGroupInvoiceEntries([...groupInvoiceEntries, shippingCost]);
    }

    for (const entry of groupInvoiceEntries) {
      const { taxRate, totalTaxAmount } = entry;
      let { totalAmount } = entry;

      let netAmount = MathUtils.normalizeSubtraction(totalAmount, totalTaxAmount);
      if (invoice.isNet) {
        netAmount = totalAmount;
        totalAmount = MathUtils.normalizeAddition(totalAmount, totalTaxAmount);
      }

      const tax = taxes.find((t) => t.taxRate === taxRate);
      if (tax) {
        tax.totalTaxAmount = MathUtils.normalizeAddition(tax.totalTaxAmount, totalTaxAmount);
        tax.totalAmount = MathUtils.normalizeAddition(tax.totalAmount, totalAmount);
        tax.netAmount = MathUtils.normalizeAddition(tax.netAmount, netAmount);
      } else {
        taxes.push({ taxRate, netAmount, totalTaxAmount, totalAmount });
      }
    }
    return taxes;
  }

  private getGroupInvoiceEntries(invoiceEntries: InvoiceEntry[]): InvoiceEntry[] {
    return invoiceEntries.reduce((groupInvoiceEntries, invoiceEntry) => {
      const newInvoiceEntry = groupInvoiceEntries.find((i) => i.taxRate === invoiceEntry.taxRate);
      if (newInvoiceEntry) {
        newInvoiceEntry.totalTaxAmount = MathUtils.normalizeAddition(newInvoiceEntry.totalTaxAmount, invoiceEntry.totalTaxAmount);
        newInvoiceEntry.totalAmount = MathUtils.normalizeAddition(newInvoiceEntry.totalAmount, invoiceEntry.totalAmount);
      } else {
        groupInvoiceEntries.push(new InvoiceEntry(invoiceEntry));
      }
      return groupInvoiceEntries;
    }, []);
  }
}
